import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useFleetMode } from "@/lib/tms/fleet-mode-context";
import {
  DEFAULT_FLEET_MODE,
  FLEET_MODE_LABELS,
  FLEET_MODE_SETTINGS_COPY,
  isFleetMode,
  type FleetMode,
} from "@/lib/tms/fleet-mode";
import { cn } from "@/lib/utils";
import { Loader2, Network, Star, Truck } from "lucide-react";
import * as React from "react";

const FLEET_MODE_OPTIONS: Array<{
  value: FleetMode;
  label: string;
  badge?: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  examples: string[];
}> = [
  {
    value: "third_party_only",
    label: "Carrier Network",
    badge: "Recommended",
    description:
      "Use Shippeek for general carrier shipping: live rates, bookings, tracking, connected carriers, addresses, and templates.",
    icon: Network,
    examples: ["UPS", "FedEx", "USPS", "Estes", "DHL"],
  },
  {
    value: "own_fleet_only",
    label: "Own Fleet TMS",
    description:
      "Use Shippeek as a fleet management workspace for dispatch, trucks, drivers, trailers, exceptions, and telematics.",
    icon: Truck,
    examples: ["Dispatch", "Fleet", "Samsara", "Motive"],
  },
];

export default function SettingsPage() {
  const { fleetMode, loading, setFleetMode } = useFleetMode();
  const [selected, setSelected] = React.useState<FleetMode>(DEFAULT_FLEET_MODE);
  const [saving, setSaving] = React.useState(false);
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!loading) {
      setSelected(fleetMode);
    }
  }, [fleetMode, loading]);

  const handleSave = async () => {
    setSaving(true);
    setSuccessMsg(null);
    setErrorMsg(null);
    try {
      const res = await fetch("/api/tms/settings", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fleet_mode: selected }),
      });
      const json = await res.json();
      if (json.status === "success") {
        const nextMode = json.data?.fleet_mode;
        const mode = isFleetMode(nextMode) ? nextMode : selected;
        setFleetMode(mode);
        setSelected(mode);
        setSuccessMsg("Workspace mode saved. Navigation and routes updated.");
      } else {
        setErrorMsg(json.error ?? "Unable to save settings.");
      }
    } catch {
      setErrorMsg("Unable to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const isDirty = selected !== fleetMode;
  const selectedModeCopy = FLEET_MODE_SETTINGS_COPY[selected];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose which Shippeek branch your team should see.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workspace Mode</CardTitle>
          <CardDescription>
            Keep the product focused by showing either carrier-network tools or own-fleet TMS tools.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="size-4 animate-spin" />
              <span>Loading…</span>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {FLEET_MODE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const active = selected === option.value;

                return (
                  <label
                    key={option.value}
                    className={cn(
                      "group relative flex min-h-56 cursor-pointer flex-col rounded-2xl border p-4 transition-[background-color,border-color,box-shadow,transform] duration-150 active:scale-[0.96]",
                      active
                        ? "border-primary bg-accent/30 ring-2 ring-primary"
                        : "border-border hover:border-foreground/30 hover:bg-muted/30",
                    )}
                  >
                    <input
                      type="radio"
                      name="fleet_mode"
                      value={option.value}
                      checked={active}
                      onChange={() => setSelected(option.value)}
                      className="sr-only"
                    />

                    <div className="flex items-start justify-between gap-3">
                      <div className="grid size-11 place-items-center rounded-xl bg-muted text-muted-foreground group-hover:text-foreground">
                        <Icon className="size-5" />
                      </div>
                      {option.badge ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                          <Star className="size-3 fill-current" />
                          {option.badge}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-4 min-w-0 space-y-2">
                      <p className="text-base font-semibold">
                        {option.label}
                        {active ? (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {FLEET_MODE_LABELS[option.value]}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-sm leading-relaxed text-muted-foreground">{option.description}</p>
                    </div>

                    <div className="mt-auto flex flex-wrap gap-2 pt-4">
                      {option.examples.map((example) => (
                        <span
                          key={example}
                          className="rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground"
                        >
                          {example}
                        </span>
                      ))}
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          <div className="rounded-xl border bg-muted/20 p-4">
            <p className="text-sm font-medium">{selectedModeCopy.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{selectedModeCopy.description}</p>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {selectedModeCopy.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-2">
                  <span className="mt-1.5 size-1.5 rounded-full bg-foreground/60" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          {successMsg ? (
            <p className="text-sm text-emerald-600 dark:text-emerald-400">{successMsg}</p>
          ) : null}
          {errorMsg ? (
            <p className="text-sm text-destructive">{errorMsg}</p>
          ) : null}

          <div className="flex justify-end border-t pt-4">
            <Button
              onClick={() => void handleSave()}
              disabled={saving || loading || !isDirty}
            >
              {saving ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : null}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
